import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import TableShell, { SortableTableTh, TableActionColumnHeader } from "../components/TableShell";
import ListPaginationBar from "../components/ListPaginationBar";
import ColumnCustomizerModal from "../components/ColumnCustomizerModal";
import ConfirmationModal from "../components/ConfirmationModal";
import ContactFollowUpStatusBadge from "../components/ContactFollowUpStatusBadge";
import ErrorBanner from "../components/ErrorBanner";
import ProfileInfoModal from "../components/ProfileInfoModal";
import PhotographyConsentBadge from "../components/PhotographyConsentBadge";
import SearchableSelect from "../components/SearchableSelect";
import {
  IconClose,
  IconReopen,
  IconView,
  iconBtnStyle,
  RowActionIcons,
} from "../shared/icons/IconLib";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { useListPagination } from "../shared/hooks/useListPagination";
import { useClearInvalidFilterSelection } from "../shared/hooks/useClearInvalidFilterSelection";
import { useLatestRequestSeq } from "../shared/hooks/useLatestRequestSeq";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { fetchTablePreferences, saveTablePreferences } from "../shared/api/tablePreferences";
import { fetchEvents } from "../shared/api/events";
import { fetchPrograms } from "../shared/api/programs";
import { fetchParticipantProfileDetails } from "../shared/api/participantProfileDetails";
import {
  completeParticipantContactLogFollowUp,
  fetchOpenParticipantContactFollowUps,
  reopenParticipantContactLogFollowUp,
} from "../shared/api/participantContactLogs";
import { downloadCsvTextFromString } from "../shared/utils/csvFileHelpers";
import { buildParticipantProfileDetailsCsvExportText } from "../shared/utils/participantProfileDetailsCsv";
import {
  eventStatusesForApi,
  filterEventsByOpenClosedStatus,
} from "../shared/utils/eventOpenClosedStatus";
import { formatEventFilterOptionLabel } from "../shared/utils/eventFilterOptionLabel";
import {
  FILTER_SELECTION_ALL,
} from "../shared/utils/filterSelectionValidity";
import { EVENT_ATTENDANCE_FILTER_OPTIONS } from "../shared/utils/eventAttendanceOptions";
import EventStatusFilterDropdown from "../components/EventStatusFilterDropdown";
import {
  PARTICIPANT_PROFILE_DETAILS_DEFAULT_VISIBLE,
  PARTICIPANT_PROFILE_DETAILS_TABLE_COLUMNS,
  PARTICIPANT_PROFILE_DETAILS_TABLE_KEY,
  formatParticipantProfileName,
  getParticipantProfileDetailsDisplay,
  getParticipantProfileDetailsSortValue,
  matchesParticipantProfileDetailsSearch,
} from "../shared/utils/participantProfileDetailsDisplay";
import {
  getParticipantContactLogDisplay,
  isParticipantContactFollowUpQueueFilter,
  matchesParticipantContactFollowUpSearch,
  PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_ALL,
  PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_DONE,
  PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_HAS_NOTES,
  PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OPTIONS,
  PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OVERDUE,
  PARTICIPANT_CONTACT_FOLLOW_UP_QUEUE_COLUMNS,
  sortParticipantContactLogRows,
} from "../shared/utils/participantContactLog";

function sortParticipantProfileRows(rows, sortConfig) {
  if (!sortConfig?.key) return rows;
  const key = sortConfig.key;
  const decorated = rows.map((o) => ({
    __orig: o,
    [key]: getParticipantProfileDetailsSortValue(o, key),
  }));
  return sortRowsByConfig(decorated, sortConfig).map((r) => r.__orig);
}

function EventParticipantDetails() {
  const { userInfo } = useAuth();
  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id;
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const paramEventId = (searchParams.get("eventId") || "").trim();
  const beginRowsRequest = useLatestRequestSeq();

  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(() =>
    paramEventId ? paramEventId : FILTER_SELECTION_ALL
  );
  const [filterProgramId, setFilterProgramId] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    if (paramEventId) return new Set();
    const statusParam = (searchParams.get("status") || "").toLowerCase();
    if (statusParam === "closed") return new Set(["Closed"]);
    if (statusParam === "expired") return new Set(["Expired"]);
    if (statusParam === "open") return new Set(["Open"]);
    if (statusParam === "all") return new Set();
    return new Set(["Open"]);
  });
  const [attendanceFilter, setAttendanceFilter] = useState(() => {
    const a = (searchParams.get("attendance") || "").trim();
    return EVENT_ATTENDANCE_FILTER_OPTIONS.includes(a) ? a : "";
  });
  const [followUpFilter, setFollowUpFilter] = useState(PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_ALL);

  const [rows, setRows] = useState([]);
  const [followUpRows, setFollowUpRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followUpSavingId, setFollowUpSavingId] = useState("");
  const [profileParticipant, setProfileParticipant] = useState(null);
  const [profileInitialTab, setProfileInitialTab] = useState("");

  const isFollowUpQueueMode = isParticipantContactFollowUpQueueFilter(followUpFilter);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState(() => [
    ...PARTICIPANT_PROFILE_DETAILS_DEFAULT_VISIBLE,
  ]);
  const [columnPrefsSaving, setColumnPrefsSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const {
    selectedIds,
    setSelectedIds,
    searchTerm,
    setSearchTerm,
    sortConfig,
    setSortConfig,
    bannerError,
    setBannerError,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({
    initialSort: { key: "participant", direction: "asc" },
  });

  const eventsForEventFilter = useMemo(
    () => filterEventsByOpenClosedStatus(events, statusFilter),
    [events, statusFilter]
  );

  const eventStatuses = useMemo(() => eventStatusesForApi(statusFilter), [statusFilter]);
  const statusFilteredEventIds = useMemo(
    () => (eventStatuses ? eventsForEventFilter.map((ev) => String(ev._id)) : null),
    [eventStatuses, eventsForEventFilter]
  );

  const loadEvents = useCallback(async () => {
    if (!account_id) {
      setEvents([]);
      setEventsLoaded(true);
      return;
    }
    setEventsLoaded(false);
    try {
      const list = await fetchEvents({ account_id });
      setEvents(list);
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
      setEvents([]);
    } finally {
      setEventsLoaded(true);
    }
  }, [account_id, setBannerError]);

  const loadPrograms = useCallback(async () => {
    if (!account_id) {
      setPrograms([]);
      return;
    }
    try {
      const list = await fetchPrograms({ account_id });
      setPrograms(list);
    } catch {
      setPrograms([]);
    }
  }, [account_id]);

  const loadRows = useCallback(async () => {
    if (!account_id) {
      setRows([]);
      return;
    }
    if (isFollowUpQueueMode) {
      setRows([]);
      return;
    }
    if (!selectedEventId || selectedEventId === "") {
      setRows([]);
      return;
    }
    if (selectedEventId === FILTER_SELECTION_ALL && Array.isArray(statusFilteredEventIds) && statusFilteredEventIds.length === 0) {
      setRows([]);
      return;
    }
    const req = beginRowsRequest();
    setLoading(true);
    setBannerError("");
    try {
      const list = await fetchParticipantProfileDetails({
        account_id,
        event_id: selectedEventId === FILTER_SELECTION_ALL ? null : selectedEventId,
        event_ids:
          selectedEventId === FILTER_SELECTION_ALL && Array.isArray(statusFilteredEventIds)
            ? statusFilteredEventIds
            : null,
        event_statuses: null,
        program_id: (filterProgramId || "").trim() || null,
        attendance: (attendanceFilter || "").trim() || null,
      });
      if (!req.isCurrent()) return;
      setRows(list);
    } catch (err) {
      if (!req.isCurrent()) return;
      setBannerError(getMessageForFetchError(err));
      setRows([]);
    } finally {
      if (!req.isCurrent()) return;
      setLoading(false);
    }
  }, [
    account_id,
    isFollowUpQueueMode,
    selectedEventId,
    statusFilteredEventIds,
    filterProgramId,
    attendanceFilter,
    setBannerError,
    beginRowsRequest,
  ]);

  const loadFollowUpRows = useCallback(async () => {
    if (!account_id || !isFollowUpQueueMode) {
      setFollowUpRows([]);
      return;
    }
    const req = beginRowsRequest();
    setLoading(true);
    setBannerError("");
    try {
      const list = await fetchOpenParticipantContactFollowUps({
        account_id,
        scope: followUpFilter,
        as_of_date: dayjs().format("YYYY-MM-DD"),
      });
      if (!req.isCurrent()) return;
      setFollowUpRows(list);
    } catch (err) {
      if (!req.isCurrent()) return;
      setBannerError(getMessageForFetchError(err));
      setFollowUpRows([]);
    } finally {
      if (!req.isCurrent()) return;
      setLoading(false);
    }
  }, [account_id, isFollowUpQueueMode, followUpFilter, setBannerError, beginRowsRequest]);

  useEffect(() => {
    loadEvents();
    loadPrograms();
  }, [loadEvents, loadPrograms]);

  useEffect(() => {
    if (isFollowUpQueueMode) {
      loadFollowUpRows();
    } else {
      loadRows();
    }
  }, [isFollowUpQueueMode, loadFollowUpRows, loadRows]);

  useEffect(() => {
    if (!isFollowUpQueueMode) {
      setSortConfig({ key: "participant", direction: "asc" });
      return;
    }
    if (followUpFilter === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_HAS_NOTES) {
      setSortConfig({ key: "created_date", direction: "desc" });
      return;
    }
    if (followUpFilter === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_DONE) {
      setSortConfig({ key: "follow_up_date", direction: "desc" });
      return;
    }
    setSortConfig({ key: "follow_up_date", direction: "asc" });
  }, [isFollowUpQueueMode, followUpFilter, setSortConfig]);

  useEffect(() => {
    if (!paramEventId) return;
    if (!eventsLoaded) return;
    const exists = events.some((ev) => String(ev._id) === String(paramEventId));
    const idStr = String(paramEventId);
    if (exists && idStr !== selectedEventId) {
      setSelectedEventId(idStr);
    }
  }, [paramEventId, events, eventsLoaded, selectedEventId]);

  useClearInvalidFilterSelection({
    selectedId: selectedEventId,
    setSelectedId: setSelectedEventId,
    optionList: eventsForEventFilter,
    optionsReady: eventsLoaded,
    preserveSelectedId: paramEventId,
    allValue: FILTER_SELECTION_ALL,
  });

  useEffect(() => {
    let cancelled = false;
    async function loadPrefs() {
      if (!account_id || !user_id) return;
      try {
        const ids = await fetchTablePreferences(
          account_id,
          user_id,
          PARTICIPANT_PROFILE_DETAILS_TABLE_KEY
        );
        if (cancelled) return;
        if (Array.isArray(ids) && ids.length > 0) {
          const allowed = new Set(PARTICIPANT_PROFILE_DETAILS_TABLE_COLUMNS.map((c) => c.id));
          const filtered = ids.filter((id) => allowed.has(id));
          if (filtered.length > 0) setVisibleColumnIds(filtered);
        }
      } catch {
        // Non-blocking; keep defaults.
      }
    }
    loadPrefs();
    return () => {
      cancelled = true;
    };
  }, [account_id, user_id]);

  const filteredRows = useMemo(
    () => (rows || []).filter((row) => matchesParticipantProfileDetailsSearch(row, searchTerm)),
    [rows, searchTerm]
  );

  const filteredFollowUpRows = useMemo(
    () =>
      (followUpRows || []).filter((row) =>
        matchesParticipantContactFollowUpSearch(row, searchTerm)
      ),
    [followUpRows, searchTerm]
  );

  const sortedRows = useMemo(
    () => sortParticipantProfileRows(filteredRows, sortConfig),
    [filteredRows, sortConfig]
  );

  const sortedFollowUpRows = useMemo(
    () => sortParticipantContactLogRows(filteredFollowUpRows, sortConfig),
    [filteredFollowUpRows, sortConfig]
  );

  const activeSortedRows = isFollowUpQueueMode ? sortedFollowUpRows : sortedRows;

  const {
    pageItems: paginatedRows,
    paginationBarProps,
    getSelectAllCheckboxProps,
  } = useListPagination(activeSortedRows, [
    isFollowUpQueueMode ? followUpRows.length : rows.length,
    searchTerm,
    sortConfig.key,
    sortConfig.direction,
    selectedEventId,
    statusFilteredEventIds ? statusFilteredEventIds.join(",") : "",
    filterProgramId,
    attendanceFilter,
    followUpFilter,
  ]);

  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, activeSortedRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);
  const isEmpty = !loading && activeSortedRows.length === 0;
  const hasEventFilters =
    (selectedEventId && selectedEventId !== FILTER_SELECTION_ALL) ||
    Boolean(statusFilteredEventIds) ||
    Boolean((filterProgramId || "").trim()) ||
    Boolean((attendanceFilter || "").trim());

  function openFollowUpProfile(log) {
    setProfileInitialTab("communication");
    setProfileParticipant({
      _id: log.participant_user_id,
      user_id: log.participant_user_id,
      first_name: log.participant_first_name,
      last_name: log.participant_last_name,
      preferred_name: log.participant_preferred_name,
    });
  }

  async function toggleFollowUpDone(log) {
    if (!account_id || !log?._id || !log.follow_up_date) return;
    setFollowUpSavingId(String(log._id));
    setBannerError("");
    try {
      if (log.follow_up_completed_date) {
        await reopenParticipantContactLogFollowUp({ account_id, _id: String(log._id) });
      } else {
        await completeParticipantContactLogFollowUp({ account_id, _id: String(log._id) });
      }
      await loadFollowUpRows();
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setFollowUpSavingId("");
    }
  }

  function followUpQueueEmptyMessage() {
    if (searchTerm.trim()) return "No notes match this search.";
    if (followUpFilter === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OVERDUE) {
      return "No overdue follow-ups.";
    }
    if (followUpFilter === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_DONE) {
      return "No done follow-ups.";
    }
    if (followUpFilter === PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_HAS_NOTES) {
      return "No communication notes yet.";
    }
    return "No open follow-ups.";
  }

  function exportSelectedCsv() {
    if (selectedVisibleCount === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one participant to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const idSet = selectedVisibleIds;
      const selected = sortedRows.filter((row) => row && idSet.has(row._id));
      if (selected.length === 0) {
        setConfirmDialog({
          open: true,
          title: "Export",
          message: "No matching participants found for the current selection.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      const csv = buildParticipantProfileDetailsCsvExportText(selected);
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsvTextFromString(`participants-details-export-${datePart}.csv`, csv);
      setBannerError("");
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  function renderCell(colId, row) {
    if (colId === "participant") {
      return (
        <button
          type="button"
          className="btn-link"
          onClick={() => {
            setProfileInitialTab("");
            setProfileParticipant(row);
          }}
          title="View profile"
        >
          {formatParticipantProfileName(row)}
        </button>
      );
    }
    if (colId === "photo_consent") {
      return <PhotographyConsentBadge consent={row?.photography_consent} />;
    }
    return getParticipantProfileDetailsDisplay(row, colId);
  }

  function renderFollowUpCell(colId, row) {
    if (colId === "participant") {
      return (
        <button
          type="button"
          className="btn-link"
          onClick={() => openFollowUpProfile(row)}
          title="View communication notes"
        >
          {getParticipantContactLogDisplay(row, "participant")}
        </button>
      );
    }
    if (colId === "follow_up_status") {
      return <ContactFollowUpStatusBadge log={row} />;
    }
    return getParticipantContactLogDisplay(row, colId);
  }

  return (
    <div className="page-content">
      <div className="page-top">
        <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} />
        <ColumnCustomizerModal
          open={columnModalOpen}
          onClose={() => setColumnModalOpen(false)}
          allColumns={PARTICIPANT_PROFILE_DETAILS_TABLE_COLUMNS}
          visibleIds={visibleColumnIds}
          onChangeVisibleIds={setVisibleColumnIds}
          saving={columnPrefsSaving}
          onReset={async () => {
            const defaultIds = [...PARTICIPANT_PROFILE_DETAILS_DEFAULT_VISIBLE];
            setVisibleColumnIds(defaultIds);
            if (!account_id || !user_id) {
              setColumnModalOpen(false);
              return;
            }
            try {
              setColumnPrefsSaving(true);
              await saveTablePreferences(
                account_id,
                user_id,
                PARTICIPANT_PROFILE_DETAILS_TABLE_KEY,
                defaultIds
              );
            } catch (err) {
              setConfirmDialog({
                open: true,
                title: "Participant Profiles columns",
                message: err?.message || "Failed to reset column preferences",
                mode: "alert",
                onConfirm: null,
              });
            } finally {
              setColumnPrefsSaving(false);
            }
            setColumnModalOpen(false);
          }}
          onSave={async (ids) => {
            if (!account_id || !user_id) {
              setVisibleColumnIds(ids);
              setColumnModalOpen(false);
              return;
            }
            try {
              setColumnPrefsSaving(true);
              await saveTablePreferences(
                account_id,
                user_id,
                PARTICIPANT_PROFILE_DETAILS_TABLE_KEY,
                ids
              );
              setVisibleColumnIds(ids);
              setColumnModalOpen(false);
            } catch (err) {
              setConfirmDialog({
                open: true,
                title: "Participant Profiles columns",
                message: err?.message || "Failed to save column preferences",
                mode: "alert",
                onConfirm: null,
              });
            } finally {
              setColumnPrefsSaving(false);
            }
          }}
        />
        <ConfirmationModal
          open={confirmDialog.open}
          title={confirmDialog.title || "Confirm"}
          message={confirmDialog.message}
          mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
          confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
          onConfirm={() => {
            const fn = confirmDialog.onConfirm;
            closeConfirmDialog();
            if (fn) fn();
          }}
          onCancel={closeConfirmDialog}
        />
        <div className="toolbar">
          <span className="toolbar-label">Event</span>
          <div className="input-min-200" style={{ minWidth: 200 }}>
            <SearchableSelect
              value={selectedEventId}
              onChange={(v) => {
                setSelectedEventId(String(v || FILTER_SELECTION_ALL));
                setSelectedIds(new Set());
              }}
              options={eventsForEventFilter.map((e) => ({
                value: String(e._id),
                label: formatEventFilterOptionLabel(e),
              }))}
              placeholder="— All —"
              searchPlaceholder="Search event…"
              allowEmpty={true}
              emptyLabel="— All —"
              maxVisibleItems={5}
              dropdownPortal={true}
              title="Event filter"
              disabled={isFollowUpQueueMode}
            />
          </div>
          <span className="toolbar-label">Program</span>
          <div className="input-min-160" style={{ minWidth: 160 }}>
            <SearchableSelect
              value={filterProgramId}
              onChange={(v) => {
                setFilterProgramId(String(v || ""));
                setSelectedIds(new Set());
              }}
              options={(programs || []).map((g) => ({
                value: String(g._id),
                label: String(g?.name || "Program"),
              }))}
              placeholder="— All —"
              searchPlaceholder="Search program…"
              allowEmpty={true}
              emptyLabel="— All —"
              maxVisibleItems={4}
              dropdownPortal={true}
              title="Program filter"
              disabled={isFollowUpQueueMode}
            />
          </div>
          <EventStatusFilterDropdown
            value={statusFilter}
            onChange={(next) => {
              setStatusFilter(next);
              setSelectedIds(new Set());
            }}
            disabled={isFollowUpQueueMode}
          />
          <label htmlFor="epd-attendance-filter" className="toolbar-filter-group">
            <span className="toolbar-label">Attendance:</span>
            <select
              id="epd-attendance-filter"
              value={attendanceFilter}
              onChange={(e) => {
                setAttendanceFilter(e.target.value);
                setSelectedIds(new Set());
              }}
              className="input select input-min-130"
              disabled={isFollowUpQueueMode}
            >
              <option value="">— All —</option>
              {EVENT_ATTENDANCE_FILTER_OPTIONS.filter((x) => x !== "").map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="epd-follow-up-filter" className="toolbar-filter-group">
            <span className="toolbar-label">Follow-up:</span>
            <select
              id="epd-follow-up-filter"
              value={followUpFilter}
              onChange={(e) => {
                setFollowUpFilter(e.target.value);
                setSelectedIds(new Set());
                setSearchTerm("");
              }}
              className="input select input-min-160"
              title="Contact-note follow-ups across participants"
            >
              {PARTICIPANT_CONTACT_FOLLOW_UP_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="toolbar">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={
              isFollowUpQueueMode
                ? "Search participant, note, type, event…"
                : "Search participant, parent, school district, zip…"
            }
            className="input input-search-grow"
          />
          <button type="button" onClick={() => setSearchTerm("")} className="btn btn-secondary btn-sm">
            Clear
          </button>
          {!isFollowUpQueueMode ? (
            <>
              <span className="toolbar-divider" />
              <button
                type="button"
                className="btn btn-md btn-primary-outline"
                disabled={selectedVisibleCount === 0 || exporting}
                onClick={() => exportSelectedCsv()}
              >
                {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
              </button>
            </>
          ) : null}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {!isEmpty && !loading ? <ListPaginationBar {...paginationBarProps} /> : null}
        {isFollowUpQueueMode ? (
          <TableShell
            loading={loading}
            isEmpty={isEmpty}
            emptyMessage={followUpQueueEmptyMessage()}
            aria-label="Contact notes and follow-ups"
          >
            <table>
              <thead>
                <tr>
                  {PARTICIPANT_CONTACT_FOLLOW_UP_QUEUE_COLUMNS.map((col) => (
                    <SortableTableTh
                      key={col.id}
                      sortKey={col.id}
                      currentSortKey={sortConfig.key}
                      direction={sortConfig.direction}
                      onSort={handleSort}
                      className={col.cellClass || undefined}
                    >
                      {col.label}
                    </SortableTableTh>
                  ))}
                  <TableActionColumnHeader align="left" />
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row) => {
                  const id = row._id;
                  const saving = followUpSavingId === String(id);
                  const hasFollowUp = !!row.follow_up_date;
                  const isDone = !!row.follow_up_completed_date;
                  return (
                    <tr key={String(id)}>
                      {PARTICIPANT_CONTACT_FOLLOW_UP_QUEUE_COLUMNS.map((col) => (
                        <td key={col.id} className={col.cellClass || undefined}>
                          {renderFollowUpCell(col.id, row)}
                        </td>
                      ))}
                      <td>
                        <RowActionIcons>
                          {hasFollowUp ? (
                            <button
                              type="button"
                              className="btn-link"
                              style={iconBtnStyle}
                              title={isDone ? "Reopen follow-up" : "Close follow-up"}
                              disabled={saving}
                              onClick={() => toggleFollowUpDone(row)}
                            >
                              {isDone ? <IconReopen size={18} /> : <IconClose size={18} />}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn-link"
                            style={iconBtnStyle}
                            title="View notes"
                            onClick={() => openFollowUpProfile(row)}
                          >
                            <IconView size={18} />
                          </button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableShell>
        ) : (
          <TableShell
            loading={loading}
            isEmpty={isEmpty}
            emptyMessage={
              searchTerm.trim()
                ? "No participants match this search."
                : hasEventFilters
                  ? "No participants match these event filters."
                  : "No participant profiles yet."
            }
            aria-label="Participant Profiles"
          >
            <table>
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 36, minWidth: 36 }}>
                    <input
                      type="checkbox"
                      {...getSelectAllCheckboxProps(handleSelectAll, selectedIds)}
                      title="Select all on this page"
                    />
                  </th>
                  {visibleColumnIds.map((colId) => {
                    const col = PARTICIPANT_PROFILE_DETAILS_TABLE_COLUMNS.find((c) => c.id === colId);
                    return (
                      <SortableTableTh
                        key={colId}
                        sortKey={colId}
                        currentSortKey={sortConfig.key}
                        direction={sortConfig.direction}
                        onSort={handleSort}
                      >
                        {col ? col.label : colId}
                      </SortableTableTh>
                    );
                  })}
                  <TableActionColumnHeader
                    align="left"
                    onCustomizeColumns={() => setColumnModalOpen(true)}
                    customizeDisabled={columnPrefsSaving}
                  />
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row) => {
                  const id = row._id;
                  return (
                    <tr key={String(id)}>
                      <td className="text-center" style={{ width: 36, minWidth: 36 }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(id)}
                          onChange={(e) => handleSelectOne(id, e.target.checked)}
                          title="Select row"
                        />
                      </td>
                      {visibleColumnIds.map((colId) => (
                        <td key={colId}>{renderCell(colId, row)}</td>
                      ))}
                      <td>
                        <RowActionIcons>
                          <button
                            type="button"
                            className="btn-link"
                            style={iconBtnStyle}
                            title="View"
                            onClick={() => {
                              setProfileInitialTab("");
                              setProfileParticipant(row);
                            }}
                          >
                            <IconView size={18} />
                          </button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableShell>
        )}
      </div>
      <ProfileInfoModal
        open={!!profileParticipant}
        onClose={() => {
          setProfileParticipant(null);
          setProfileInitialTab("");
          if (isFollowUpQueueMode) loadFollowUpRows();
        }}
        showAllRegistrationFields
        initialTab={profileInitialTab}
        participantUserId={profileParticipant?.user_id || profileParticipant?._id}
        relatedEventId={
          selectedEventId && selectedEventId !== FILTER_SELECTION_ALL ? selectedEventId : ""
        }
        firstName={profileParticipant?.first_name}
        lastName={profileParticipant?.last_name}
        preferredName={profileParticipant?.preferred_name}
        email={profileParticipant?.email}
        phone={profileParticipant?.phone}
        birthday={profileParticipant?.birthday}
        gender={profileParticipant?.gender}
        grade={profileParticipant?.grade}
        school={profileParticipant?.school}
        schoolDistrict={profileParticipant?.school_district}
        raceEthnicity={profileParticipant?.race_ethnicity}
        raceEthnicityOther={profileParticipant?.race_ethnicity_other}
        photographyConsent={profileParticipant?.photography_consent}
        parentName={profileParticipant?.parent_name}
        parentFirstName={profileParticipant?.parent_first_name}
        parentLastName={profileParticipant?.parent_last_name}
        parentEmail={profileParticipant?.parent_email}
        parentPhone={profileParticipant?.parent_phone}
        parentPreferredName={profileParticipant?.parent_preferred_name}
        parentGender={profileParticipant?.parent_gender}
        parentZipCode={profileParticipant?.parent_zip_code}
        parentHighestEducationLevel={profileParticipant?.parent_highest_education_level}
        parentHearAboutUs={profileParticipant?.parent_hear_about_us}
        parentHearAboutUsOther={profileParticipant?.parent_hear_about_us_other}
        createdDate={profileParticipant?.created_date}
      />
    </div>
  );
}

export default EventParticipantDetails;
